import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { type FC, useCallback, useEffect, useState } from 'react';
import { Badge } from 'react-bootstrap';
import Dropdown from 'react-bootstrap/Dropdown';
import { Eye, PencilSquare, Trash } from 'react-bootstrap-icons';
import { config } from '../../../modules/config';
import { useAuth, useNotifications } from '../../../modules/hooks';
import type { LockGroupRecord } from '../../../modules/interfaces';
import { Can } from '../../../modules/utils/can';
import { CanMulti } from '../../CanMulti';
import { DotDropDown } from '../../Customize/DropDown';
import { useLockGroups } from '../../Dashboards/LockManagement/useLockGroups';
import { ConfirmModal, type ModalState } from '../../Modals/Confirm';
import { LockGroupModal, type LockGroupModalMode } from '../../Modals/LockGroup';
import { DataTable } from '../DataTable';

const LOCK_SUBJECT = 'lock_management';

interface GroupDotMenuProps {
  data?: LockGroupRecord;
  onView: (record: LockGroupRecord) => void;
  onEdit: (record: LockGroupRecord) => void;
  onDelete: (record: LockGroupRecord) => void;
}

const GroupDotMenu: FC<GroupDotMenuProps> = ({ data, onView, onEdit, onDelete }) => {
  if (!data) return null;
  return (
    <CanMulti actions={['update', 'delete']} subject={LOCK_SUBJECT}>
      <DotDropDown title="" drop="end">
        <Dropdown.Item onClick={() => onView(data)}>
          <Eye className="dropdown-icon me-1" />
          View
        </Dropdown.Item>
        <Can I="update" a={LOCK_SUBJECT}>
          <Dropdown.Item onClick={() => onEdit(data)}>
            <PencilSquare className="dropdown-icon me-1" />
            Edit
          </Dropdown.Item>
        </Can>
        <Can I="delete" a={LOCK_SUBJECT}>
          <Dropdown.Item onClick={() => onDelete(data)}>
            <Trash className="dropdown-icon me-1" />
            Delete
          </Dropdown.Item>
        </Can>
      </DotDropDown>
    </CanMulti>
  );
};

export const LockGroupsTable: FC = () => {
  const { setSimpleToasts } = useNotifications()!;
  const { credentialsInfo } = useAuth()!;
  const groupsService = useLockGroups();

  const [groups, setGroups] = useState<LockGroupRecord[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<LockGroupModalMode>('create');
  const [activeRecord, setActiveRecord] = useState<LockGroupRecord>();
  const [confirmModal, setConfirmModal] = useState<ModalState>({
    show: false,
    title: '',
    body: '',
    handler: () => {},
  });

  const loadGroups = useCallback(async () => {
    if (!credentialsInfo?.token) return;
    try {
      setGroups(await groupsService.list());
    } catch {
      setSimpleToasts({
        type: 'danger',
        message: 'Failed to load lock groups',
        show: true,
      });
    }
  }, [credentialsInfo?.token, groupsService, setSimpleToasts]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const onAdd = useCallback(() => {
    setActiveRecord(undefined);
    setModalMode('create');
    setShowModal(true);
  }, []);

  const onView = useCallback((record: LockGroupRecord) => {
    setActiveRecord(record);
    setModalMode('view');
    setShowModal(true);
  }, []);

  const onEdit = useCallback((record: LockGroupRecord) => {
    setActiveRecord(record);
    setModalMode('edit');
    setShowModal(true);
  }, []);

  const onDelete = useCallback(
    (record: LockGroupRecord) => {
      setConfirmModal({
        show: true,
        title: 'Delete this lock group?',
        body: 'This action cannot be undone.',
        handler: async () => {
          setConfirmModal((prev) => ({ ...prev, show: false }));
          await groupsService.remove(record.uuid);
          await loadGroups();
          setSimpleToasts({ type: 'success', message: 'Lock group deleted', show: true });
        },
      });
    },
    [loadGroups, groupsService, setSimpleToasts],
  );

  const [columns] = useState<ColDef<LockGroupRecord>[]>([
    { field: 'name', headerName: 'Group name', flex: 3 },
    { field: 'description', headerName: 'Description', flex: 3 },
    {
      headerName: 'Scope',
      flex: 2,
      cellRenderer: (params: ICellRendererParams<LockGroupRecord>) =>
        params.data?.appliesToAllRooms ? (
          <Badge bg="primary">All rooms</Badge>
        ) : (
          <Badge bg="secondary">{params.data?.rooms?.length ?? 0} rooms</Badge>
        ),
    },
    {
      headerName: 'Locks',
      flex: 1,
      valueGetter: (params) => params.data?.locks?.length ?? 0,
    },
    {
      headerName: 'ACTION',
      colId: 'action',
      cellClass: 'actionCol justify-content-end',
      flex: 1,
      cellRenderer: (params: ICellRendererParams<LockGroupRecord>) => (
        <GroupDotMenu
          data={params.data}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ),
    },
  ]);

  return (
    <>
      <div className="my-custom-table">
        <DataTable
          addButtonFunction={onAdd}
          addButtonText="Add lock group"
          component={LOCK_SUBJECT}
          refreshHandler={loadGroups}
          changeState={0}
          rowData={groups}
          columnDefs={columns}
          pagination
          paginationPageSize={config.tables.balances.paginationSize}
        />
      </div>

      <LockGroupModal
        show={showModal}
        mode={modalMode}
        data={activeRecord}
        handleClose={() => setShowModal(false)}
        onSaved={loadGroups}
      />

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        body={confirmModal.body}
        handleCancel={() => setConfirmModal((prev) => ({ ...prev, show: false }))}
        handleConfirm={() => confirmModal.handler?.()}
      />
    </>
  );
};
